import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  getActionIntent,
  executeDirectAction,
  executeFlowAction,
  resolveSipPagingZone,
} from '../../services/homeLauncher/launcherActionService';
import { flowDisabledUserMessage } from '../../utils/flowEnabled';
import { waitingDisplayLabel } from '../../utils/executionStatusDisplay';
import {
  dispatchAction,
  getMyImageDisplayActions,
  getTenantDevices,
  createImageDisplayUploadUrl,
  dispatchImageDisplayCommand,
  isOption2ImageDisplayEnabled,
  getCollaborationWorkspace,
  getContactList,
} from '../../api/backendApi';
import { openScreenSaver } from '../../components/ambient/screenSaverNavigation';
import { ROUTES } from '../../utils/constants';
import { isPhoneEnabledForUser } from '../../utils/phoneEntitlement';
import { isRemindersEnabledForUser } from '../../utils/remindersEntitlement';
import { isEmailRemEnabledForUser } from '../../utils/emailRemEntitlement';
import { putImageToSignedUrl } from '../../utils/imageDisplayUpload';

/**
 * Manages all action-dispatch state and handlers.
 * Extracted from HomeLauncherPage – execution logic is identical.
 *
 * @param {object} opts
 * @param {boolean}  opts.editMode        – from useLauncherEditMode
 * @param {string}   opts.tenantId        – from useAuth
 * @param {Function} opts.openIncidentById – from useIncident
 * @param {Function} opts.navigate        – from useNavigate
 * @param {string|null|undefined} opts.preferredSiteId – optional explicit site for Screen Saver
 * @param {object|null|undefined} opts.userData – Firestore user doc (phone entitlement)
 * @param {Function} [opts.onOpenAskSb] – open the shortcuts sidebar on the Ask SB tab
 */
export default function useLauncherActions({
  editMode,
  tenantId,
  openIncidentById,
  navigate,
  preferredSiteId = null,
  userData = null,
  role = null,
  onOpenAskSb = null,
}) {
  const [rtpDialogOpen,          setRtpDialogOpen]          = useState(false);
  const [activePagingIcon,       setActivePagingIcon]       = useState(null);
  const [rtpManualDialogOpen,    setRtpManualDialogOpen]    = useState(false);
  const [sipDialogOpen,          setSipDialogOpen]          = useState(false);
  const [activeSipPagingIcon,    setActiveSipPagingIcon]    = useState(null);
  const [activeSipUri,           setActiveSipUri]           = useState(null);
  const [imageDisplayDialogOpen, setImageDisplayDialogOpen] = useState(false);
  const [activeImageDisplayIcon, setActiveImageDisplayIcon] = useState(null);
  const [imageDisplayLoading,    setImageDisplayLoading]    = useState(false);
  const [collaborationModalOpen, setCollaborationModalOpen] = useState(false);
  const [activeCollaborationData, setActiveCollaborationData] = useState(null);
  const [contactListDialogOpen,   setContactListDialogOpen]   = useState(false);
  const [activeContactListId,     setActiveContactListId]     = useState(null);
  const [contactListCallOpen,     setContactListCallOpen]     = useState(false);
  const [activeContactListCall,   setActiveContactListCall]   = useState(null);
  const [phoneOpen,               setPhoneOpen]               = useState(false);
  const [remindersOpen,           setRemindersOpen]           = useState(false);
  const [emailRemOpen,            setEmailRemOpen]            = useState(false);

  const handleIconPress = useCallback(async (icon) => {
    if (editMode) return;

    const intent = getActionIntent(icon);

    if (intent.type === 'paging') {
      setActivePagingIcon(icon);
      setRtpDialogOpen(true);
      return;
    }

    if (intent.type === 'sipPaging') {
      try {
        const { sipUri } = await resolveSipPagingZone(tenantId, icon);
        setActiveSipPagingIcon(icon);
        setActiveSipUri(sipUri);
        setSipDialogOpen(true);
      } catch (e) {
        console.error('[HomeLauncher] Failed to load SIP paging zone', e);
        toast.error(e.message || 'Failed to load SIP paging zone');
      }
      return;
    }

    if (intent.type === 'imageDisplay') {
      setActiveImageDisplayIcon(icon);
      setImageDisplayDialogOpen(true);
      return;
    }

    if (intent.type === 'error') {
      if (intent.debug) {
        console.error('[HomeLauncher] Button misconfigured: flowId is UUID-like', intent.debug);
      }
      toast.error(intent.message);
      return;
    }

    if (intent.type === 'settings') {
      navigate('/home/settings');
      return;
    }

    if (intent.type === 'openScreenSaver') {
      openScreenSaver(navigate, {
        siteId: preferredSiteId,
        returnTo: ROUTES.HOME_LAUNCHER,
      });
      return;
    }

    if (intent.type === 'none') {
      toast.info('No flow assigned');
      return;
    }

    if (intent.type === 'collaboration') {
      try {
        const workspace = await getCollaborationWorkspace(tenantId, intent.icon.collaborationWorkspaceId);
        if (!workspace) {
          toast.error('Collaboration workspace not found');
          return;
        }
        setActiveCollaborationData({
          workspaceId: workspace.id,
          workspace,
          workspaceName: workspace.name,
        });
        setCollaborationModalOpen(true);
      } catch (e) {
        console.error('[HomeLauncher] Failed to load collaboration workspace', e);
        toast.error('Failed to open collaboration workspace');
      }
      return;
    }

    if (intent.type === 'phone') {
      if (!isPhoneEnabledForUser(userData, role)) {
        toast.error('Browser phone is not enabled for your account.');
        return;
      }
      setPhoneOpen(true);
      return;
    }

    if (intent.type === 'reminders') {
      if (!isRemindersEnabledForUser(userData)) {
        toast.error('Reminders and timers are not enabled for your account.');
        return;
      }
      setRemindersOpen(true);
      return;
    }

    if (intent.type === 'emailRem') {
      if (!isEmailRemEnabledForUser(userData)) {
        toast.error('Email reminders are not enabled for your account.');
        return;
      }
      setEmailRemOpen(true);
      return;
    }

    if (intent.type === 'askSb') {
      onOpenAskSb?.();
      return;
    }

    if (intent.type === 'action') {
      try {
        await executeDirectAction(icon, tenantId);
        toast.success(`${icon.label} triggered`);
      } catch (e) {
        console.error('[HomeLauncher] Action dispatch failed', e);
        toast.error(e.message || 'Failed to run action');
      }
      return;
    }

    if (intent.type === 'contact_list') {
      try {
        const resp = await getContactList(tenantId, intent.icon.contactListId);
        const contactList = resp?.data?.contactList || resp?.contactList || resp;
        if (!contactList?.id) {
          toast.error('Contact list not found or has been deleted.');
          return;
        }
        if (contactList.calling?.enabled) {
          const sessionId =
            (typeof crypto !== 'undefined' && crypto.randomUUID)
              ? crypto.randomUUID()
              : `${Date.now()}_${Math.random().toString(16).slice(2)}`;
          setActiveContactListCall({ contactList, sessionId });
          setContactListCallOpen(true);
        } else {
          setActiveContactListId(contactList.id);
          setContactListDialogOpen(true);
        }
      } catch (e) {
        console.error('[HomeLauncher] Failed to load contact list', e);
        toast.error('Failed to open contact list');
      }
      return;
    }

    // intent.type === 'flow'
    try {
      const { incidentIds, result: res } = await executeFlowAction(icon, tenantId);

      if (res?.executionStatus === 'waiting') {
        toast.info(waitingDisplayLabel(res.resumeAt || res.details?.resumeAt));
        return;
      }

      if (incidentIds.length > 0) {
        toast.success(`${icon.label} triggered (incidents: ${incidentIds.length})`);
        console.info('[HomeLauncher] Flow triggered incidents', { incidentIds });
        if (openIncidentById) {
          try {
            const opened = await openIncidentById(incidentIds[0]);
            if (!opened) {
              console.warn('[HomeLauncher] Incident created but could not be opened', {
                incidentId: incidentIds[0],
              });
            }
          } catch (e) {
            console.warn('[HomeLauncher] Failed to open incident modal after flow', {
              incidentId: incidentIds[0],
              error: e,
            });
          }
        }
      } else {
        toast.success(`${icon.label} triggered`);
        console.info('[HomeLauncher] Flow trigger completed (no incidentIds in response)', {
          summary: {
            executionStatus:   res?.executionStatus,
            success:           res?.success,
            flowId:            res?.details?.flowId,
            executionId:       res?.details?.executionId,
            totalActions:      res?.details?.totalActions,
            successfulActions: res?.details?.successfulActions,
          },
        });
      }
    } catch (e) {
      console.error('[HomeLauncher] Flow execution failed', e);
      toast.error(flowDisabledUserMessage(e) || e.message || 'Failed to trigger flow');
    }
  }, [editMode, tenantId, openIncidentById, navigate, preferredSiteId, userData, role, onOpenAskSb]);

  /**
   * Handle image display modal submission.
   * Extracted from the large inline onSubmit in HomeLauncherPage – logic is identical.
   */
  const handleImageDisplaySubmit = useCallback(async ({ imageBase64, overlayText, fitMode, filename }) => {
    setImageDisplayLoading(true);
    try {
      if (!tenantId) throw new Error('Tenant is required');
      if (!activeImageDisplayIcon?.imageDisplayActionId) {
        throw new Error('Image Display action is not configured');
      }

      const actions      = await getMyImageDisplayActions(tenantId);
      const selectedAction = actions.find((a) => a && a.id === activeImageDisplayIcon.imageDisplayActionId) || null;
      const targetDeviceId = selectedAction?.targetDeviceId || null;
      if (!targetDeviceId) throw new Error('Image Display action has no target device');

      const devices  = await getTenantDevices(tenantId);
      const device   = devices.find((d) => d && d.id === targetDeviceId) || null;
      const deviceIp = device?.ipAddress || device?.ip || device?.ip_address || null;
      if (!deviceIp) throw new Error('deviceIp is required for image_display');

      const agentId   = device?.agentId   || device?.agent_id   || null;
      const segmentId = device?.segmentId || device?.segment_id || device?.segment
        || device?.properties?.segment || null;

      const dataUrl = String(imageBase64 || '').trim();
      if (!dataUrl) throw new Error('Image is required');

      const ctMatch     = dataUrl.match(/^data:([^;]+);base64,/i);
      const contentType = (ctMatch && ctMatch[1]) || 'image/png';

      if (!isOption2ImageDisplayEnabled()) {
        const resp = await dispatchAction({
          tenantId,
          actionId: activeImageDisplayIcon.imageDisplayActionId,
          context: {
            imageBase64: dataUrl,
            overlayText,
            fitMode,
            filename,
            deviceIp,
            ...(agentId ? { agentId } : {}),
          },
        });
        const ok = Boolean(resp && resp.success && resp.result && resp.result.success);
        if (!ok) {
          throw new Error((resp?.result?.message || resp?.result?.error) || 'Dispatch failed');
        }
        toast.success('Image sent to display');
        setImageDisplayDialogOpen(false);
        setActiveImageDisplayIcon(null);
        return;
      }

      const uploadMeta = await createImageDisplayUploadUrl({ tenantId, filename, contentType });
      if (!uploadMeta?.uploadUrl || !uploadMeta?.downloadUrl) {
        throw new Error('Failed to create upload URL');
      }

      await putImageToSignedUrl({
        uploadUrl: uploadMeta.uploadUrl,
        headers: uploadMeta.requiredHeaders || { 'Content-Type': contentType },
        dataUrl,
        contentType,
      });

      console.log('[HomeLauncher][ImageDisplay] Pre-dispatch', {
        actionId:      activeImageDisplayIcon.imageDisplayActionId,
        targetDeviceId,
        deviceIp,
        agentId,
      });

      const resp = await dispatchImageDisplayCommand({
        tenantId,
        deviceId: targetDeviceId,
        ...(segmentId ? { segmentId } : {}),
        ...(agentId   ? { agentId }   : {}),
        imageUrl:    uploadMeta.downloadUrl,
        filename,
        overlayText,
      });
      const ok = Boolean(resp && resp.success);
      if (!ok) throw new Error((resp?.error || resp?.message) || 'Dispatch failed');

      toast.success('Image sent to display');
      setImageDisplayDialogOpen(false);
      setActiveImageDisplayIcon(null);
    } catch (e) {
      console.error('[HomeLauncher] Image display failed', e);
      toast.error(e?.message ? `Failed to send image to display: ${e.message}` : 'Failed to send image to display');
    } finally {
      setImageDisplayLoading(false);
    }
  }, [tenantId, activeImageDisplayIcon]);

  return {
    rtpDialogOpen,          setRtpDialogOpen,
    activePagingIcon,       setActivePagingIcon,
    rtpManualDialogOpen,    setRtpManualDialogOpen,
    sipDialogOpen,          setSipDialogOpen,
    activeSipPagingIcon,    setActiveSipPagingIcon,
    activeSipUri,           setActiveSipUri,
    imageDisplayDialogOpen, setImageDisplayDialogOpen,
    activeImageDisplayIcon, setActiveImageDisplayIcon,
    imageDisplayLoading,
    collaborationModalOpen, setCollaborationModalOpen,
    activeCollaborationData, setActiveCollaborationData,
    contactListDialogOpen,   setContactListDialogOpen,
    activeContactListId,     setActiveContactListId,
    contactListCallOpen,     setContactListCallOpen,
    activeContactListCall,   setActiveContactListCall,
    phoneOpen,               setPhoneOpen,
    remindersOpen,           setRemindersOpen,
    emailRemOpen,            setEmailRemOpen,
    handleIconPress,
    handleImageDisplaySubmit,
  };
}
